import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Download, Receipt, IndianRupee, TrendingUp, CheckCircle2 } from "lucide-react";
import { formatINR, formatLakhs, formatNum } from "@/lib/format";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { createPdfDoc, withDefaults, drawTotalsBlock, finalizePdf, fmtINR } from "@/lib/pdf";

const months = () => {
  const arr = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const m = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    arr.push({ value: m, label: d.toLocaleString("en-IN", { month: "long", year: "numeric" }) });
  }
  return arr;
};

export default function AdminBilling() {
  const monthOptions = months();
  const [month, setMonth] = useState(monthOptions[0].value);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [reim, setReim] = useState({ raised: 0, received: 0 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/billing?month=${month}`);
      setRows(data);
    } catch { toast.error("Failed to load billing"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [month]); // eslint-disable-line

  const openDetail = async (storeId) => {
    try {
      const { data } = await api.get(`/billing/${storeId}?month=${month}`);
      setDetail(data);
      setReim({ raised: data.reimbursement?.raised || 0, received: data.reimbursement?.received || 0 });
    } catch { toast.error("Failed to load statement"); }
  };

  const saveReim = async () => {
    setSaving(true);
    try {
      await api.post("/billing/reimbursement", { store_id: detail.store.id, month, raised: reim.raised, received: reim.received });
      toast.success("Reimbursement updated");
      load();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const exportPDF = async () => {
    if (!detail) return;
    const doc = await createPdfDoc();
    doc.setFontSize(16); doc.setTextColor(225, 29, 42);
    doc.text("Coke@9 OCT — Client Invoice", 12, 14);
    doc.setFontSize(9); doc.setTextColor(60);
    doc.text("Powered by BBIPL for CCI", 12, 20);
    doc.text(`Store: ${detail.store.store_name} (${detail.store.store_code})`, 12, 26);
    doc.text(`City: ${detail.store.city}`, 12, 31);
    doc.text(`Month: ${month}`, 12, 36);
    doc.text(`For: Coca-Cola India (CCI)`, 12, 41);

    doc.setFontSize(11); doc.setTextColor(20);
    doc.text("Sales summary", 12, 50);
    autoTable(doc, withDefaults({
      startY: 53,
      head: [["Cups sold", "Revenue", "Days operated"]],
      body: [[formatNum(detail.sales_summary.glasses), fmtINR(detail.sales_summary.revenue), detail.sales_summary.days]],
    }));

    doc.text("Billable Purchases (BIB / CO2)", 12, doc.lastAutoTable.finalY + 8);
    autoTable(doc, withDefaults({
      startY: doc.lastAutoTable.finalY + 11,
      head: [["Date", "Item", "Qty", "Rate", "Amount"]],
      body: detail.purchases.map((p) => [p.date, p.item, p.quantity, fmtINR(p.rate), fmtINR(p.amount)]),
      headStyles: { fillColor: [225,29,42], textColor: [255,255,255], font: "NotoSans", fontStyle: "bold" },
    }));

    doc.text("Billable Expenses", 12, doc.lastAutoTable.finalY + 8);
    autoTable(doc, withDefaults({
      startY: doc.lastAutoTable.finalY + 11,
      head: [["Date", "Category", "Amount", "Notes"]],
      body: detail.expenses.map((e) => [e.date, e.category, fmtINR(e.amount), e.notes || "—"]),
      headStyles: { fillColor: [225,29,42], textColor: [255,255,255], font: "NotoSans", fontStyle: "bold" },
    }));

    drawTotalsBlock(doc, [
      { label: "Total Purchases", value: fmtINR(detail.total_purchase) },
      { label: "Total Expenses", value: fmtINR(detail.total_expense) },
      { label: "Total Billable to Client", value: fmtINR(detail.total_billable) },
    ], { title: "Invoice totals" });

    finalizePdf(doc, { footer: `Generated by Coke@9 OCT - powered By BBIPL · ${new Date().toLocaleString("en-IN")}` });
    doc.save(`invoice_${detail.store.store_code}_${month}.pdf`);
    toast.success("Invoice PDF downloaded");
  };

  const totals = rows.reduce((a, r) => ({
    purchase: a.purchase + r.purchase_total,
    expense: a.expense + r.expense_total,
    billable: a.billable + r.billable_total,
    raised: a.raised + r.raised,
    received: a.received + r.received,
  }), { purchase: 0, expense: 0, billable: 0, raised: 0, received: 0 });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Billing & Invoicing" subtitle="Monthly client statements — Coca-Cola India" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Statement period</div>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger data-testid="billing-month-select" className="w-56 h-12"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="rounded-xl bg-white border border-gray-200 px-4 py-3 min-w-[140px]">
                <div className="text-[11px] uppercase tracking-widest font-bold text-gray-500">Total Billable</div>
                <div className="font-display text-xl font-black text-brand-red">{formatLakhs(totals.billable)}</div>
              </div>
              <div className="rounded-xl bg-white border border-gray-200 px-4 py-3 min-w-[140px]">
                <div className="text-[11px] uppercase tracking-widest font-bold text-gray-500">Raised</div>
                <div className="font-display text-xl font-black">{formatLakhs(totals.raised)}</div>
              </div>
              <div className="rounded-xl bg-white border border-gray-200 px-4 py-3 min-w-[140px]">
                <div className="text-[11px] uppercase tracking-widest font-bold text-gray-500">Received</div>
                <div className="font-display text-xl font-black text-green-700">{formatLakhs(totals.received)}</div>
              </div>
              <div className="rounded-xl bg-white border border-gray-200 px-4 py-3 min-w-[140px]">
                <div className="text-[11px] uppercase tracking-widest font-bold text-gray-500">Outstanding</div>
                <div className="font-display text-xl font-black text-amber-700">{formatLakhs(totals.raised - totals.received)}</div>
              </div>
            </div>
          </div>

          <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="billing-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3">Store</th>
                    <th className="text-left px-4 py-3">City</th>
                    <th className="text-right px-4 py-3">Purchases</th>
                    <th className="text-right px-4 py-3">Expenses</th>
                    <th className="text-right px-4 py-3">Billable</th>
                    <th className="text-right px-4 py-3">Raised</th>
                    <th className="text-right px-4 py-3">Received</th>
                    <th className="text-center px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={8} className="text-center py-8 text-gray-500">Loading…</td></tr>}
                  {!loading && rows.map(r => (
                    <tr key={r.store_id} className="border-t border-gray-100" data-testid={`billing-row-${r.store_no}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{r.store_name}</div>
                        <div className="text-xs text-gray-500">{r.store_code}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{r.city}</td>
                      <td className="px-4 py-3 text-right">{formatINR(r.purchase_total)}</td>
                      <td className="px-4 py-3 text-right">{formatINR(r.expense_total)}</td>
                      <td className="px-4 py-3 text-right font-bold text-brand-red">{formatINR(r.billable_total)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatINR(r.raised)}</td>
                      <td className="px-4 py-3 text-right text-green-700">{formatINR(r.received)}</td>
                      <td className="px-4 py-3 text-center">
                        <Button data-testid={`view-statement-${r.store_no}`} size="sm" variant="outline"
                          onClick={() => openDetail(r.store_id)}>
                          <FileText className="h-3.5 w-3.5 mr-1" /> Statement
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </main>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent data-testid="statement-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl">
          {detail && <>
            <DialogHeader>
              <DialogTitle className="font-display">
                Monthly Statement — {detail.store.store_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-gray-50 p-3 border border-gray-100">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Cups sold</div>
                  <div className="font-display text-2xl font-black">{formatNum(detail.sales_summary.glasses)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 border border-gray-100">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Revenue</div>
                  <div className="font-display text-2xl font-black">{formatLakhs(detail.sales_summary.revenue)}</div>
                </div>
                <div className="rounded-lg bg-red-50 p-3 border border-red-200">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-red-800">Total billable</div>
                  <div className="font-display text-2xl font-black text-brand-red">{formatLakhs(detail.total_billable)}</div>
                </div>
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500 mb-2">Billable purchases</div>
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs">
                      <tr><th className="text-left p-2">Date</th><th className="text-left p-2">Item</th>
                        <th className="text-right p-2">Qty</th><th className="text-right p-2">Rate</th><th className="text-right p-2">Amount</th></tr>
                    </thead>
                    <tbody>
                      {detail.purchases.map(p => (
                        <tr key={p.id} className="border-t border-gray-100">
                          <td className="p-2">{p.date}</td><td className="p-2">{p.item}</td>
                          <td className="p-2 text-right">{p.quantity}</td>
                          <td className="p-2 text-right">{formatINR(p.rate)}</td>
                          <td className="p-2 text-right font-semibold">{formatINR(p.amount)}</td>
                        </tr>
                      ))}
                      {detail.purchases.length === 0 && <tr><td colSpan={5} className="p-3 text-center text-gray-400">No purchases</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500 mb-2">Billable expenses</div>
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs">
                      <tr><th className="text-left p-2">Date</th><th className="text-left p-2">Category</th>
                        <th className="text-right p-2">Amount</th></tr>
                    </thead>
                    <tbody>
                      {detail.expenses.map(e => (
                        <tr key={e.id} className="border-t border-gray-100">
                          <td className="p-2">{e.date}</td><td className="p-2">{e.category}</td>
                          <td className="p-2 text-right font-semibold">{formatINR(e.amount)}</td>
                        </tr>
                      ))}
                      {detail.expenses.length === 0 && <tr><td colSpan={3} className="p-3 text-center text-gray-400">No expenses</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-xl bg-gray-50 p-4 space-y-3">
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Reimbursement tracker</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-600">Raised (₹)</label>
                    <Input data-testid="reim-raised-input" inputMode="numeric" value={reim.raised}
                      onChange={(e) => setReim({ ...reim, raised: Number(e.target.value.replace(/\D/g,"")) || 0 })} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Received (₹)</label>
                    <Input data-testid="reim-received-input" inputMode="numeric" value={reim.received}
                      onChange={(e) => setReim({ ...reim, received: Number(e.target.value.replace(/\D/g,"")) || 0 })} />
                  </div>
                </div>
                <Button data-testid="save-reim-btn" size="sm" onClick={saveReim} disabled={saving}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Save
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
              <Button data-testid="export-pdf-btn" onClick={exportPDF} className="bg-brand-red hover:bg-[#B31721]">
                <Download className="h-4 w-4 mr-2" /> Export PDF invoice
              </Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
